import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import twoFactorService from "../api/twoFactorService";
import { useAuth } from "../context/AuthContext";

const TwoFactorVerifyLoginPage = () => {
    const navigate = useNavigate();
    const { token, login } = useAuth();

    const userId = sessionStorage.getItem("2fa:userId");

    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // ===============================
    // Auto-redirect logic
    // ===============================
    useEffect(() => {
        if (token) {
            navigate("/dashboard");
            return;
        }

        if (!userId) {
            navigate("/login");
        }
    }, [token, userId, navigate]);

    // ===============================
    // Auto-submit when 6 digits
    // ===============================
    useEffect(() => {
        if (code.length === 6 && !loading) {
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (loading) return;

        setError("");

        if (!userId || code.length !== 6) return;

        try {
            setLoading(true);

            console.log("🔐 Verifying 2FA for user:", userId);

            const res = await twoFactorService.verify2FA(
                Number(userId),
                code
            );

            if (!res?.token) {
                throw new Error("Token not returned");
            }

            // Save token
            login(res.token);

            // Save role from JWT
            try {
                const decoded = jwtDecode(res.token);

                const role =
                    decoded.role ||
                    decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

                if (role) {
                    localStorage.setItem("role", role.toLowerCase());
                }
            } catch {
                console.warn("JWT decode failed");
            }

            sessionStorage.removeItem("2fa:userId");

            console.log("✅ 2FA success");
            navigate("/dashboard");
        } catch (err) {
            console.error("❌ 2FA failed:", err);

            if (err?.response?.status === 401) {
                setError("Invalid or expired code");
            } else {
                setError("Verification failed. Try again.");
            }

            setCode("");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <div className="w-full max-w-md bg-white p-6 rounded-lg shadow">
                <h1 className="text-xl font-bold text-center mb-2">
                    Two-Factor Authentication
                </h1>

                <p className="text-sm text-gray-600 text-center mb-4">
                    Enter the 6-digit code from your authenticator app
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => {
                            setError("");
                            setCode(e.target.value.replace(/\D/g, ""));
                        }}
                        className="w-full border rounded px-4 py-3 text-center text-lg tracking-widest"
                        placeholder="••••••"
                        disabled={loading}
                    />

                    {error && (
                        <p className="text-red-600 text-sm text-center">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading || code.length !== 6}
                        className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-60"
                    >
                        {loading ? "Verifying..." : "Verify"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default TwoFactorVerifyLoginPage;
